import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MessageSquarePlus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'; // Added ChevronUp
import { ThreatProfileResponse } from '../../lib/types/threatProfile';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  citations?: string[];
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  timestamp: number;
}

interface PerplexityChatProps {
  threatProfile: ThreatProfileResponse | null;
  apiKey: string;
  initialPrompt?: string;
  initialChatTitle?: string; // New prop for the initial chat title
  onInitialPromptConsumed?: () => void;
}

const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const processMessageWithCitations = (rawText: string, citations: string[] | undefined): string => {
  if (!citations || citations.length === 0) return rawText;
  let processedText = rawText;
  citations.forEach((url, i) => {
    if (url) {
      const placeholder = `[${i + 1}]`;
      const placeholderRegex = new RegExp(escapeRegExp(placeholder) + "(?!\\s*\\()", "g");
      processedText = processedText.replace(placeholderRegex, `${placeholder}(${url})`);
    }
  });
  return processedText;
};

const CitationLinkRenderer: React.FC<React.PropsWithChildren<any>> = ({ node, href, children, ...props }) => {
  const textContent = React.Children.toArray(children).join('');
  if (href && /^\d+$/.test(textContent)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block px-[5px] py-[3px] mx-[4px] text-xs font-normal bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-[4px] hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors no-underline"
        style={{ fontSize: '0.7rem', lineHeight: '1' }}
        {...props}
      >
        {textContent}
      </a>
    );
  }
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" {...props}>{children}</a>;
};

const PerplexityChat: React.FC<PerplexityChatProps> = ({ threatProfile, apiKey, initialPrompt, initialChatTitle, onInitialPromptConsumed }) => {
  const [allConversations, setAllConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [currentChatMessages, setCurrentChatMessages] = useState<Message[]>([]);
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [chatUiActive, setChatUiActive] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const storedConversations = localStorage.getItem('cyraAiConversations');
    if (storedConversations) {
      try {
        const parsedConversations: Conversation[] = JSON.parse(storedConversations).map((conv: any) => ({
          ...conv,
          messages: conv.messages.map((msg: any) => ({...msg, timestamp: new Date(msg.timestamp)}))
        }));
        setAllConversations(parsedConversations);
      } catch (e) {
        console.error("Failed to parse conversations from localStorage", e);
        localStorage.removeItem('cyraAiConversations');
      }
    }
  }, []);

  useEffect(() => {
    if (allConversations.length > 0) {
      const conversationsToStore = allConversations.map(conv => ({
        ...conv,
        messages: conv.messages.map(msg => ({...msg, timestamp: msg.timestamp.toISOString()}))
      }));
      localStorage.setItem('cyraAiConversations', JSON.stringify(conversationsToStore));
    } else {
        localStorage.removeItem('cyraAiConversations');
    }
  }, [allConversations]);

  useEffect(() => {
    if (activeConversationId) {
      const activeConvo = allConversations.find(c => c.id === activeConversationId);
      setCurrentChatMessages(activeConvo ? activeConvo.messages : []);
      if (!chatUiActive) setChatUiActive(true);
    } else {
      setCurrentChatMessages([]);
    }
  }, [activeConversationId, allConversations, chatUiActive]);


  const baseSystemPrompt = `You are Cyra (say that when people ask who you are), an AI expert in all fields related to cybersecurity. You were created by Pax Intelligence. If someone asks who are you, this is what you should say. Your primary function is to provide accurate responses on everything related to cybersecurity.
**Before answering any user query, first determine if it is related to cybersecurity.**
If the query IS related to cybersecurity, provide a comprehensive and accurate answer.
If the query IS NOT related to cybersecurity, you MUST respond with: 'I am an AI assistant specializing in cybersecurity. I can only answer questions related to this field.' Do not answer the unrelated query. Provide only short and consice answers.`;

  const getSystemPromptContent = () => {
    let prompt = baseSystemPrompt;
    if (threatProfile) {
      prompt += `\n\n---BEGIN THREAT PROFILE DATA---\n${JSON.stringify(threatProfile, null, 2)}\n---END THREAT PROFILE DATA---`;
    } else {
      prompt += "\n\nNo specific threat profile data is currently loaded for the user.";
    }
    return prompt;
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };
  
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const createNewConversation = () => {
    setInput('');
    setActiveConversationId(null); 
    setCurrentChatMessages([]); 
    setChatUiActive(true); 
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
    }
  };
  
  const deleteConversation = (conversationId: string) => {
    setAllConversations(prev => prev.filter(convo => convo.id !== conversationId));
    if (activeConversationId === conversationId) {
      setActiveConversationId(null);
      setCurrentChatMessages([]);
      if (allConversations.length === 1) {
          setChatUiActive(false);
      }
    }
  };

  const handleSubmit = async (
    e?: React.FormEvent<HTMLFormElement>,
    promptOverride?: string,
    forceNewConversation?: boolean // New parameter
  ) => {
    if (e) e.preventDefault();
    const messageContent = promptOverride || input;
    if (!messageContent.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
    };

    const assistantMessageId = (Date.now() + 1).toString();
    const assistantPlaceholderMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      citations: [],
      timestamp: new Date(),
    };

    let currentConversationId = activeConversationId;
    let newConversationCreated = false;

    if (forceNewConversation) {
      currentConversationId = null; // Override if forcing a new conversation
    }

    if (!currentConversationId) {
      currentConversationId = Date.now().toString() + "_conv";
      const conversationTitle = (promptOverride && initialChatTitle) ? initialChatTitle : messageContent.substring(0, 30) + (messageContent.length > 30 ? "..." : "");
      const newConversation: Conversation = {
        id: currentConversationId,
        title: conversationTitle,
        messages: [userMessage, assistantPlaceholderMessage],
        timestamp: Date.now(),
      };
      setAllConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(currentConversationId);
      newConversationCreated = true;
    } else {
      setAllConversations(prev =>
        prev.map(convo =>
          convo.id === currentConversationId
            ? { ...convo, messages: [...convo.messages, userMessage, assistantPlaceholderMessage], timestamp: Date.now() }
            : convo
        )
      );
       setCurrentChatMessages(prev => [...prev, userMessage, assistantPlaceholderMessage]);
    }
    
    if (!chatUiActive) {
      setChatUiActive(true);
    }
    setInput('');
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);
    setError(null);
    
    let historyForApi: { role: 'user' | 'assistant' | 'system'; content: string }[] = [];

    if (!newConversationCreated && currentConversationId) {
      const activeConvo = allConversations.find(c => c.id === currentConversationId);
      if (activeConvo) {
        historyForApi = activeConvo.messages
          .slice(0, -2) 
          .filter(msg => (msg.role === 'user') || (msg.role === 'assistant' && msg.content.trim() !== ''))
          .map(msg => ({ role: msg.role, content: msg.content }));
      }
    }

    const apiMessagesPayload = [
      { role: 'system', content: getSystemPromptContent() },
      ...historyForApi,
      { role: userMessage.role, content: userMessage.content }
    ];

    let currentAssistantResponseText = '';
    let currentAssistantCitations: string[] = [];

    try {
      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json', 'Accept': 'text/event-stream', 'Referer': 'https://www.perplexity.ai/' },
        body: JSON.stringify({ model: 'sonar-pro', messages: apiMessagesPayload, stream: true }),
      });

      if (!response.ok) { throw new Error(`API Error: ${response.status}`); }
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) { break; }
        lineBuffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = lineBuffer.indexOf('\n')) >= 0) {
          const line = lineBuffer.substring(0, newlineIndex);
          lineBuffer = lineBuffer.substring(newlineIndex + 1);
          if (line.trim() === '' || !line.startsWith('data:')) continue;
          const jsonDataString = line.substring('data:'.length).trim();
          if (jsonDataString === '[DONE]' || jsonDataString === '') continue;
          try {
            const parsedJson = JSON.parse(jsonDataString);
            if (parsedJson.choices?.[0]?.delta?.content) currentAssistantResponseText += parsedJson.choices[0].delta.content;
            if (parsedJson.citations && Array.isArray(parsedJson.citations)) {
                const newCitations = parsedJson.citations.filter((c: any) => typeof c === 'string');
                if (newCitations.length > 0) currentAssistantCitations = [...new Set([...currentAssistantCitations, ...newCitations])];
            }
            if (parsedJson.event === 'perplexity_citations' && parsedJson.data?.citations && Array.isArray(parsedJson.data.citations)) {
                 const eventCitations = parsedJson.data.citations.filter((c: any) => typeof c === 'string');
                 if (eventCitations.length > 0) currentAssistantCitations = [...new Set([...currentAssistantCitations, ...eventCitations])];
            }
            
            const finalConvId = currentConversationId;
            setAllConversations(prevConvos => prevConvos.map(convo => {
                if (convo.id === finalConvId) {
                    return {
                        ...convo,
                        messages: convo.messages.map(msg => msg.id === assistantMessageId ? { ...msg, content: currentAssistantResponseText, citations: [...currentAssistantCitations] } : msg)
                    };
                }
                return convo;
            }));
          } catch (e) { console.error('Error parsing stream data JSON:', e, `Problematic JSON string: "${jsonDataString}"`); }
        }
      }
    } catch (err) { 
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        setError(errorMessage);
        const finalConvId = currentConversationId;
        setAllConversations(prevConvos => prevConvos.map(convo => {
            if (convo.id === finalConvId) {
                return {
                    ...convo,
                    messages: convo.messages.map(msg => msg.id === assistantMessageId ? { ...msg, content: `Error: ${errorMessage}` } : msg)
                };
            }
            return convo;
        }));
    } finally { setIsLoading(false); }
  };

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [currentChatMessages]);

  useEffect(() => {
    if (initialPrompt && threatProfile && !isLoading) {
      if (!chatUiActive) {
        setChatUiActive(true);
      }
      
      // Force a new conversation for "Ask Cyra" clicks
      setActiveConversationId(null); // This ensures handleSubmit creates a new conversation
      setCurrentChatMessages([]); // Clear messages from any previous active chat in the UI immediately

      setInput(initialPrompt);
      handleSubmit(undefined, initialPrompt, true); // Pass true for forceNewConversation

      if (onInitialPromptConsumed) {
        onInitialPromptConsumed();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, initialChatTitle, threatProfile, apiKey]); // Ensure all dependencies that might trigger this are listed


  const sortedConversations = [...allConversations].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="flex h-full w-full"> {/* This is the root, should not have card styling itself */}
      {/* Sidebar */}
      <div className="w-1/4 min-w-[280px] max-w-[320px] border-r bg-transparent px-4 pt-4 pb-0 flex flex-col"> {/* bg-transparent so parent card bg shows */}
        {chatUiActive && (
          <Button onClick={createNewConversation} className="w-full mb-4">
            <MessageSquarePlus className="mr-2 h-4 w-4" /> New Chat
          </Button>
        )}
        <p className="text-sm text-muted-foreground mb-2 px-2">Recent Conversations</p>
        {!chatUiActive && (
          <Button
            variant="ghost"
            className="w-full justify-center py-2 mt-1"
            onClick={() => {
              setChatUiActive(true);
              if (!activeConversationId && sortedConversations.length > 0) {
                setActiveConversationId(sortedConversations[0].id);
              }
            }}
          >
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}
        {chatUiActive && (
          <>
            <ScrollArea className="flex-grow mt-2">
              {sortedConversations.map(convo => (
                <div
                  key={convo.id}
                  onClick={() => setActiveConversationId(convo.id)}
                  className={`p-3 mb-2 rounded-md cursor-pointer hover:bg-muted ${activeConversationId === convo.id ? 'bg-muted font-semibold' : ''} flex justify-between items-center group`}
                >
                  <span className="truncate text-sm">{convo.title}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); deleteConversation(convo.id); }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {allConversations.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No recent chats.</p>}
            </ScrollArea>
            <Button
              variant="ghost"
              className="w-full justify-center py-2 mt-1"
              onClick={() => {
                setChatUiActive(false);
                setActiveConversationId(null); // Also clear active conversation to hide main chat
              }}
            >
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Chat Area */}
      {/* This div replaces the Card component that was here. It should not have shadow or explicit card bg. */}
      <div className="flex-1 flex flex-col h-full m-0"> 
        <AnimatePresence>
          {chatUiActive && (
            <motion.div
              key="chat-window-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ overflow: 'hidden' }}
              className="flex-grow flex flex-col"
            >
              <CardContent className={`pt-4 flex-grow flex flex-col ${currentChatMessages.length === 0 ? "pb-0" : "pb-4"}`}>
                <ScrollArea className="h-[900px] w-full p-4 border rounded-lg mb-4 bg-muted/20 flex-grow" ref={scrollAreaRef}>
                  {currentChatMessages.map((msg) => (
                    <div key={msg.id} className={`mb-3 flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`p-3 shadow-sm max-w-[85%] break-words ${msg.role === 'user' ? 'bg-primary text-primary-foreground self-end rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-md' : 'bg-card border self-start rounded-tl-xl rounded-tr-xl rounded-br-xl rounded-bl-md'}`}>
                        {msg.role === 'assistant' ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: CitationLinkRenderer }}>{processMessageWithCitations(msg.content, msg.citations)}</ReactMarkdown></div>
                        ) : (<p className="text-sm whitespace-pre-wrap">{msg.content}</p>)}
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1 px-1 ${msg.role === 'user' ? 'self-end' : 'self-start'}`}>{msg.role === 'user' ? 'You' : 'Cyra AI'} - {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  ))}
                  {isLoading && currentChatMessages.length > 0 && currentChatMessages[currentChatMessages.length - 1].role === 'assistant' && currentChatMessages[currentChatMessages.length - 1].content === '' && (
                     <div className="mb-3 flex flex-col items-start">
                        <div className="p-3 rounded-xl shadow-sm max-w-[85%] bg-card border self-start"><p className="text-sm animate-pulse prose prose-sm dark:prose-invert">Thinking...</p></div>
                        <p className="text-xs text-muted-foreground mt-1 px-1 self-start">Cyra AI</p>
                     </div>
                  )}
                </ScrollArea>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
        <CardContent className={!chatUiActive && !activeConversationId ? "pt-6 pb-6" : "pt-0 pb-4 mt-auto"}>
          <form onSubmit={handleSubmit} className="block">
            <div className="relative flex items-end w-full">
              <Textarea ref={textareaRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="Ask anything cyber..." className="flex-grow text-base p-3 pr-12 rounded-xl border-border focus-visible:ring-1 focus-visible:ring-ring resize-none overflow-hidden min-h-[40px]" rows={1} disabled={isLoading || !threatProfile} aria-label="Chat message input" />
              <Button type="submit" disabled={isLoading || !threatProfile || !input.trim()} size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg h-8 w-8 bg-primary hover:bg-primary/90 disabled:bg-muted-foreground"><ArrowRight className="h-5 w-5 text-primary-foreground" /></Button>
            </div>
          </form>
          {!threatProfile && <p className="text-amber-600 text-sm mt-2">Chat disabled until threat profile is loaded.</p>}
          {error && <p className="text-red-600 text-sm mt-2">Error: {error}</p>}
        </CardContent>
      </div>
    </div>
  );
};

export default PerplexityChat;